import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaShieldAlt } from "react-icons/fa";
import useDeviceDetect from "../hooks/useDeviceDetect";
import "./Add.css";
import { normalizeTruckNumber } from "./tripFleet";

const normalizeViolationRecord = (record) =>
  Object.keys(record || {}).reduce((normalized, key) => {
    normalized[key.toLowerCase()] = record[key];
    return normalized;
  }, {});

const isViolationActive = (violation) => {
  const status = String(violation.violation_status || "").trim().toUpperCase();
  const resolved = String(violation.resolved || "").trim().toUpperCase();
  return status === "OPEN" || (!status && resolved !== "Y");
};

const normalizeDriverValue = (value) => String(value || "").trim().toLowerCase();
const formatEnteredTruck = (value) => {
  const entered = String(value || "").trim().toUpperCase();
  const normalized = entered.replace(/[^A-Z0-9]/g, "");
  const match = normalized.match(/^(\d+)([A-Z]+)$/);
  return match ? `${match[1]}/${match[2]}` : entered;
};

const AddRecord = () => {
  const [formData, setFormData] = useState({
    tracker: "New JP",
    sjm: "",
    journey_plane_no: "",
    journey_plane_date: "",
    scheduled_vehicle: "",
    carrier: "",
    jp_status: "IN TRANSIT",
    next_arrival_date: "",
    next_point: "HAIMA",
    ivms_check_date: "",
    ivms_point: "DUQM",
    destination: "",
    offload_point: "",
    driver_name: "",
    driverId: null,
    remarks: "",
    accommodation: "",
    truck_status: "LIVE",
    start_region: "",
    reporting_driver_code: "",
    contractor_name: "AMLS",
    origin: "DUQM",
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [driverRestStatus, setDriverRestStatus] = useState({});
  const [showRestAlert, setShowRestAlert] = useState(false);
  const [showRegisterRest, setShowRegisterRest] = useState(false);
  const [alertDriver, setAlertDriver] = useState(null);
  const [selectedDriverTemp, setSelectedDriverTemp] = useState("");
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [activeViolations, setActiveViolations] = useState([]);
  const [violationAlert, setViolationAlert] = useState(null);
  const [issueH2S, setIssueH2S] = useState(false);
  const [h2sData, setH2sData] = useState({
    device_id: "",
    phone_number: "",
    expected_return_at: "",
    condition_out: "GOOD",
    remarks: "",
    briefing_confirmed: false,
  });
  const [issueThuraya, setIssueThuraya] = useState(false);
  const [thurayaData, setThurayaData] = useState({
    device_id: "",
    phone_number: "",
    expected_return_at: "",
    condition_out: "GOOD",
    remarks: "",
    briefing_confirmed: false,
  });
  const [devicesList, setDevicesList] = useState([]);
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();

  const trackerOptions = [
    "AHMED", "MOHAMMED B", "KHALIL R", "MOHAMMED M", "MOHAMMED H",
    "KHALIL H", "ISSA", "ABDULLAH", "New JP", "ALABJAR", "MUHANNED",
  ];

  const [dropdownData, setDropdownData] = useState({
    trackers: trackerOptions,
    sjms: [],
    scheduledVehicles: [],
    carriers: [],
    nextPoints: [],
    ivmsPoints: [],
    destinations: [],
    offloadPoints: [],
    accommodation: [],
    truck_status: [],
    start_region: [],
  });

  const sjmOptions = useMemo(
    () => [
      "MOHAMMED R", "ESSAM", "ALBALOUSHI", "SAIF", "MOHAMMED A",
      "ALMONTHER", "ISSA", "KHALIL R", "KHALIL H", "MOHAMMED M",
      "ALABJAR", "HAZIM ALYAFAYI", "FAYSAL FADHIL", "MOHAMMED H",
    ],
    []
  );

  const truckOptions = useMemo(() => ["Stopped", "Live"], []);
  const regionOptions = useMemo(() => ["North", "South"], []);

  const getCurrentLocalDateTime = () => {
    const localDate = new Date();
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [activeDrivers, setActiveDrivers] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);

  const contractorOptions = ["AMLS", "Al Mukhtar"];
  const originOptions = ["Salalah", "DUQM"];

  const reloadDevices = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/device-tracker/devices`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDevicesList(res.data.data || []);
    } catch (err) {
      console.error("Error refreshing devices:", err);
    }
  }, []);

  const availableH2S = useMemo(
    () => devicesList.filter((d) => d.device_type === "H2S" && d.availability_status === "AVAILABLE" && d.operational_status === "ACTIVE"),
    [devicesList]
  );
  const availableThuraya = useMemo(
    () => devicesList.filter((d) => d.device_type === "THURAYA" && d.availability_status === "AVAILABLE" && d.operational_status === "ACTIVE"),
    [devicesList]
  );

  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const [response, fleetResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/dashboarddropdown`, { headers }),
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/trip-fleet`, { headers }),
        ]);
        const items = response.data.data || [];
        const fleet = fleetResponse.data.data || [];
        setFleetVehicles(fleet);
        if (Array.isArray(items)) {
          setDropdownData({
            trackers: [...new Set(items.map(i => i.tracker).filter(Boolean))],
            sjms: [...new Set(items.map(i => i.sjm).filter(Boolean))],
            scheduledVehicles: fleet.map((item) => item.truck_reg),
            carriers: [...new Set(fleet.map((item) => item.company_name).filter(Boolean))],
            nextPoints: [...new Set(items.map(i => i.next_point).filter(Boolean))],
            ivmsPoints: [...new Set(items.map(i => i.ivms_point).filter(Boolean))],
            destinations: [...new Set(items.map(i => i.destination).filter(Boolean))],
            offloadPoints: [...new Set(items.map(i => i.offload_point).filter(Boolean))],
            accommodation: [...new Set(items.map(i => i.accommodation).filter(Boolean))]
          });
          const dateTimeLocal = getCurrentLocalDateTime();
          setFormData((prevState) => ({
            ...prevState,
            journey_plane_date: dateTimeLocal,
            next_arrival_date: dateTimeLocal,
            ivms_check_date: dateTimeLocal,
          }));
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchActiveDrivers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/drivers-list`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const active = (response.data.data || [])
          .filter((d) => d.is_active === "Y")
          .map((d) => ({
            driver_name: d.driver_name,
            reporting_driver_code: d.reporting_driver_code || "",
            driver_id: d.driver_id,
          }));
        setActiveDrivers(active);
      } catch (error) {
        setActiveDrivers([]);
      }
    };

    const fetchActiveViolations = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/driver-violations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const rows = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        setActiveViolations(
          rows.map(normalizeViolationRecord).filter(isViolationActive)
        );
      } catch (error) {
        console.error("Error fetching active driver violations:", error);
        setActiveViolations([]);
      }
    };

    fetchDropdownData();
    fetchActiveDrivers();
    fetchActiveViolations();
    reloadDevices();
  }, [reloadDevices]);

  const findActiveDriverViolation = (driver) => {
    if (!driver) return null;
    const name = normalizeDriverValue(driver.driver_name || driver.name);
    const code = normalizeDriverValue(driver.reporting_driver_code);
    const id = normalizeDriverValue(driver.driver_id || driver.driverId);

    return activeViolations.find((violation) =>
      (name && normalizeDriverValue(violation.driver_name) === name) ||
      (code && normalizeDriverValue(violation.reporting_driver_code) === code) ||
      (id && normalizeDriverValue(violation.driver_id) === id)
    ) || null;
  };

  const showDriverViolationAlert = (driver, violation) => {
    setViolationAlert({
      driverName: driver.driver_name || driver.name || "Selected driver",
      driverCode: driver.reporting_driver_code || violation.reporting_driver_code || "",
      violationType: violation.violation || violation.violation_type || "Driver violation",
      violationDate: violation.violation_time || violation.created_at || "",
      remarks: violation.remarks || "",
      status: violation.violation_status || "OPEN",
    });
  };

  // Fetch driver rest status
  useEffect(() => {
    const fetchDriverRestStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/driver-rest-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDriverRestStatus(response.data.data || {});
      } catch (error) {
        console.error("Error fetching driver rest status:", error);
      }
    };
    fetchDriverRestStatus();
    const interval = setInterval(fetchDriverRestStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredScheduledVehicles = useMemo(() => {
    if (formData.contractor_name === "Al Mukhtar") {
      return fleetVehicles.filter((item) => item.company_name === "Al Mukhtar").map((item) => item.truck_reg);
    }
    return fleetVehicles.filter((item) => item.company_name !== "Al Mukhtar").map((item) => item.truck_reg);
  }, [fleetVehicles, formData.contractor_name]);

  const validateDriverRest = (driverName) => {
    const status = driverRestStatus[driverName];
    if (!status || status.is_expat !== 'Y') return { valid: true };

    // Only Expat drivers are subject to rest restrictions.
    if (status.current_rest_status === 'ON_REST') {
      const endsAt = status.current_rest_ends_at ? new Date(status.current_rest_ends_at) : null;
      const hoursRemaining = endsAt ? Math.max(0, Math.ceil((endsAt - new Date()) / (1000 * 60 * 60))) : null;
      return {
        valid: false,
        warning: true,
        type: 'ON_REST',
        message: `${driverName} is currently on rest period.`,
        restEndsAt: status.current_rest_ends_at,
        hoursRemaining,
      };
    }

    // SECOND: If there's an admin override and driver is NOT on rest, allow with warning
    if (status.has_active_override) {
      const expiresAt = status.override_expires_at ? new Date(status.override_expires_at) : null;
      const hoursLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60))) : null;
      return {
        valid: true,
        warning: true,
        message: `⚠️ Driver has active admin override (expires in ${hoursLeft ?? 'unknown'} hours). Can assign despite needing rest.`,
        has_override: true,
        override_expires_at: status.override_expires_at,
        hoursRemaining: hoursLeft,
      };
    }

    // Block rest-due Expat drivers unless an override is active.
    if (status.needs_rest) {
      return {
        valid: false,
        warning: true,
        type: 'NEEDS_REST',
        message: `${driverName} requires 24-hour rest after ${status.consecutive_work_days} consecutive work days.`,
        consecutive_days: status.consecutive_work_days,
        last_rest_date: status.last_rest_date,
      };
    }

    return { valid: true };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return null;
    const localDate = new Date(localDateTime);
    return localDate.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.driver_name) {
      setMessage("Driver Name is required.");
      return;
    }
    const approvedTruck = fleetVehicles.find((item) => item.truck_key === normalizeTruckNumber(formData.scheduled_vehicle));
    const enteredTruck = formatEnteredTruck(formData.scheduled_vehicle);
    const selectedDriver = activeDrivers.find(
      (driver) => driver.driver_name === formData.driver_name
    ) || {
      driver_name: formData.driver_name,
      driver_id: formData.driverId,
      reporting_driver_code: formData.reporting_driver_code,
    };
    const activeViolation = findActiveDriverViolation(selectedDriver);
    if (activeViolation) {
      showDriverViolationAlert(selectedDriver, activeViolation);
      setMessage("Journey assignment blocked: the selected driver has an active violation.");
      return;
    }

    if (issueH2S) {
      if (!h2sData.device_id) {
        setMessage("Please select an H₂S device to issue.");
        return;
      }
      if (!h2sData.briefing_confirmed) {
        setMessage("Mandatory check: Please confirm that the driver has been briefed on H₂S requirements before issuing the monitor.");
        return;
      }
    }

    if (issueThuraya) {
      if (!thurayaData.device_id) {
        setMessage("Please select a Thuraya satellite phone to issue.");
        return;
      }
      if (!thurayaData.briefing_confirmed) {
        setMessage("Mandatory check: Please confirm that the driver has been briefed on Thuraya satellite phone use before issuing the phone.");
        return;
      }
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("Unauthorized: Please log in.");
      return;
    }
    setSubmitting(true);
    const dataToSubmit = {
      ...formData,
      scheduled_vehicle: approvedTruck?.truck_reg || enteredTruck,
      carrier: String(formData.carrier || "").trim(),
      journey_plane_date: convertToUTC(formData.journey_plane_date),
      next_arrival_date: convertToUTC(formData.next_arrival_date),
      ivms_check_date: convertToUTC(formData.ivms_check_date),
      reporting_driver_code: formData.reporting_driver_code,
      contractor_name: formData.contractor_name,
      origin: formData.origin,
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/addRecord`,
        dataToSubmit,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(response.data.message);
      if (formData.journey_plane_no && formData.driver_name) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BASE_API_URL}/api/assign-driver`,
            {
              journey_plane_no: formData.journey_plane_no,
              driver_name: formData.driver_name,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (assignError) {
          if (assignError.response?.status === 400) {
            const errorData = assignError.response.data;
            setAlertDriver({
              name: formData.driver_name,
              days: errorData.consecutive_work_days,
              lastRest: errorData.last_rest_date,
              message: errorData.message,
              onRest: errorData.on_rest,
              restEndsAt: errorData.rest_ends_at,
              hoursRemaining: errorData.hours_remaining
            });
            setShowRestAlert(true);
            setSubmitting(false);
            return;
          }
        }
      }

      if (issueH2S && h2sData.device_id) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BASE_API_URL}/device-tracker/loans`,
            {
              device_id: h2sData.device_id,
              jp_number: formData.journey_plane_no,
              driver_name: formData.driver_name,
              phone_number: h2sData.phone_number || "N/A",
              offloading_location: formData.offload_point || formData.destination || "DUQM",
              issued_at: convertToUTC(formData.journey_plane_date) || new Date().toISOString(),
              expected_return_at: h2sData.expected_return_at ? convertToUTC(h2sData.expected_return_at) : null,
              condition_out: h2sData.condition_out || "GOOD",
              remarks: `Issued via Journey Plan ${formData.journey_plane_no}${h2sData.remarks ? ` - ${h2sData.remarks}` : ""}`,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (loanErr) {
          console.error("Failed to create H2S device loan:", loanErr);
        }
      }

      if (issueThuraya && thurayaData.device_id) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BASE_API_URL}/device-tracker/loans`,
            {
              device_id: thurayaData.device_id,
              jp_number: formData.journey_plane_no,
              driver_name: formData.driver_name,
              phone_number: thurayaData.phone_number || "N/A",
              offloading_location: formData.offload_point || formData.destination || "DUQM",
              issued_at: convertToUTC(formData.journey_plane_date) || new Date().toISOString(),
              expected_return_at: thurayaData.expected_return_at ? convertToUTC(thurayaData.expected_return_at) : null,
              condition_out: thurayaData.condition_out || "GOOD",
              remarks: `Issued via Journey Plan ${formData.journey_plane_no}${thurayaData.remarks ? ` - ${thurayaData.remarks}` : ""}`,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (loanErr) {
          console.error("Failed to create Thuraya device loan:", loanErr);
        }
      }

      setIssueH2S(false);
      setH2sData({ device_id: "", phone_number: "", expected_return_at: "", condition_out: "GOOD", remarks: "", briefing_confirmed: false });
      setIssueThuraya(false);
      setThurayaData({ device_id: "", phone_number: "", expected_return_at: "", condition_out: "GOOD", remarks: "", briefing_confirmed: false });
      reloadDevices();
      setTimeout(() => setMessage(""), 3000);
      const dateTimeLocal = getCurrentLocalDateTime();
      setFormData({
        tracker: "New JP",
        sjm: "",
        journey_plane_no: "",
        journey_plane_date: dateTimeLocal,
        scheduled_vehicle: "",
        carrier: "",
        jp_status: "IN TRANSIT",
        next_arrival_date: dateTimeLocal,
        next_point: "HAIMA",
        ivms_check_date: dateTimeLocal,
        ivms_point: "DUQM",
        destination: "",
        offload_point: "",
        driver_name: "",
        driverId: null,
        remarks: "",
        accommodation: "",
        truck_status: "LIVE",
        start_region: "",
        contractor_name: "AMLS",
        origin: "DUQM",
      });
    } catch (error) {
      console.error("Error details:", error.response ? error.response.data : error.message);
      setMessage(
        "Error adding record: " + (error.response ? error.response.data.message : "Network error.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDriverSelection = () => {
    if (!selectedDriverTemp) return;
    const selectedDriver = activeDrivers.find(
      (driver) => driver.driver_name === selectedDriverTemp
    );
    const activeViolation = findActiveDriverViolation(selectedDriver);
    if (activeViolation) {
      showDriverViolationAlert(selectedDriver, activeViolation);
      setShowDriverDropdown(false);
      setSelectedDriverTemp("");
      return;
    }
    const validation = validateDriverRest(selectedDriverTemp);
    if (!validation.valid) {
      setAlertDriver({
        name: selectedDriverTemp,
        days: validation.consecutive_days,
        lastRest: validation.last_rest_date,
        message: validation.message,
        onRest: validation.type === 'ON_REST',
        restEndsAt: validation.restEndsAt,
        hoursRemaining: validation.hoursRemaining
      });
      setShowRestAlert(true);
      setShowDriverDropdown(false);
      setSelectedDriverTemp("");
      return;
    }
    const chosenDriver = activeDrivers.find(d => d.driver_name === selectedDriverTemp);
    if (chosenDriver) {
      setFormData(prev => ({
        ...prev,
        driver_name: chosenDriver.driver_name,
        driverId: chosenDriver.driver_id,
        reporting_driver_code: chosenDriver.reporting_driver_code,
      }));
      if (validation.warning) {
        setMessage(validation.message);
        setTimeout(() => setMessage(""), 5000);
      }
    }
    setShowDriverDropdown(false);
    setDriverSearchTerm("");
  };

  const filteredDrivers = useMemo(() => {
    const searchTerm = driverSearchTerm.toLowerCase();
    return activeDrivers.filter(
      (driver) =>
        driver.driver_name.toLowerCase().includes(searchTerm) ||
        (driver.reporting_driver_code &&
          driver.reporting_driver_code.toLowerCase().includes(searchTerm))
    );
  }, [activeDrivers, driverSearchTerm]);

  const handleRegisterRest = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("Unauthorized: Please log in.");
      return;
    }
    const startDate = e.target.rest_start_date.value;
    const endDate = e.target.rest_end_date.value;
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/register-rest`,
        {
          driver_name: alertDriver.name,
          start_date: startDate,
          end_date: endDate || null,
          notes: `Manual rest registration. Worked ${alertDriver.days || 0} consecutive days.`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowRegisterRest(false);
      setAlertDriver(null);
      setMessage("Rest period registered successfully! Driver is now available.");
      const statusResponse = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/api/driver-rest-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDriverRestStatus(statusResponse.data.data || {});
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Register rest error:", error);
      setMessage(error.response?.data?.message || "Failed to register rest period.");
    } finally {
      setSubmitting(false);
    }
  };

  const getDriverStatusInfo = (driverName) => {
    const driver = activeDrivers.find((item) => item.driver_name === driverName);
    if (findActiveDriverViolation(driver)) {
      return {
        className: "driver-has-violation",
        badge: <span className="driver-status-badge violation">Active violation</span>,
        selectable: false,
      };
    }
    const status = driverRestStatus[driverName];
    if (!status || status.is_expat !== 'Y') return { className: "", badge: null, selectable: true };

    // Expat drivers already on rest cannot be selected.
    if (status.current_rest_status === 'ON_REST') {
      const endsAt = status.current_rest_ends_at ? new Date(status.current_rest_ends_at) : null;
      const hoursLeft = endsAt ? Math.max(0, Math.ceil((endsAt - new Date()) / (1000 * 60 * 60))) : null;
      return {
        className: "driver-on-rest",
        badge: <span className="badge badge-warning">🛌 ON REST{hoursLeft != null ? ` (${hoursLeft}h left)` : ''}</span>,
        selectable: false,
      };
    }

    // SECOND: Active override
    if (status.has_active_override) {
      const expiresAt = status.override_expires_at ? new Date(status.override_expires_at) : null;
      const hoursLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60))) : null;
      return {
        className: "driver-override",
        badge: <span className="badge badge-success">🔓 OVERRIDE ACTIVE{hoursLeft != null ? ` (${hoursLeft}h left)` : ''}</span>,
        selectable: true,
      };
    }

    // Rest-due Expat drivers cannot be selected without an override.
    if (status.needs_rest) {
      return {
        className: "driver-needs-rest",
        badge: <span className="badge badge-danger">⚠️ NEEDS REST</span>,
        selectable: false,
      };
    }

    // FOURTH: Approaching limit
    if (status.consecutive_work_days === 5) {
      return {
        className: "driver-warning",
        badge: <span className="badge badge-warning">⚠️ {status.consecutive_work_days} DAYS</span>,
        selectable: true,
      };
    }

    return { className: "", badge: null, selectable: true };
  };

  const handleContractorChange = (e) => {
    const contractor = e.target.value;
    let origin = formData.origin;
    if (contractor === "AMLS") origin = "DUQM";
    else if (contractor === "Al Mukhtar") origin = "Salalah";
    setFormData((prev) => ({
      ...prev,
      contractor_name: contractor,
      origin,
      scheduled_vehicle: "",
      carrier: "",
    }));
  };

  const handleOriginChange = (e) => {
    setFormData((prev) => ({ ...prev, origin: e.target.value }));
  };

  const handleScheduledVehicleChange = (e) => {
    const value = e.target.value;
    const fleetTruck = fleetVehicles.find((item) => item.truck_key === normalizeTruckNumber(value));
    setFormData((prev) => ({
      ...prev,
      scheduled_vehicle: value,
      carrier: fleetTruck?.company_name || "",
    }));
  };

  const handleScheduledVehicleBlur = () => {
    const fleetTruck = fleetVehicles.find((item) => item.truck_key === normalizeTruckNumber(formData.scheduled_vehicle));
    if (!fleetTruck) return;
    setFormData((prev) => ({
      ...prev,
      scheduled_vehicle: fleetTruck.truck_reg,
      carrier: prev.carrier || fleetTruck.company_name,
    }));
  };

  return (
    <div className={`containeradd ${isMobile ? "mobile" : "desktop"}`}>
      <button className="close-button" onClick={() => navigate("/main")}>×</button>
      {loading ? (
        <div>Loading dropdown data...</div>
      ) : (
        <form onSubmit={handleSubmit} className="form-grid mannual_form_grid">
          <div className="block-display">
            <label className="form-label-addrecord">Journey Plan No <span className="required">*</span></label>
            <input type="text" className="form-input-addrecord" name="journey_plane_no" value={formData.journey_plane_no} onChange={handleChange} list="journey-list" required />
          </div>
          <div>
            <label className="form-label-addrecord">Journey Plan Date & Time <span className="required">*</span></label>
            <input type="datetime-local" className="form-input-addrecord" name="journey_plane_date" value={formData.journey_plane_date} onChange={handleChange} required />
          </div>
          <div>
            <label className="form-label-addrecord">Journey Plan Status</label>
            <select className="form-input-addrecord form-select-addrecord" name="jp_status" value={formData.jp_status} onChange={handleChange}>
              <option value="">Select Status</option>
              <option value="IN TRANSIT">IN TRANSIT</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </div>
          <div>
            <label className="form-label-addrecord">Contractor Name <span className="required">*</span></label>
            <select className="form-input-addrecord form-select-addrecord" name="contractor_name" value={formData.contractor_name} onChange={handleContractorChange} required>
              {contractorOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div>
            <label className="form-label-addrecord">Origin <span className="required">*</span></label>
            <select className="form-input-addrecord form-select-addrecord" name="origin" value={formData.origin} onChange={handleOriginChange} required>
              {originOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
            </select>
          </div>
          <div>
            <label className="form-label-addrecord">Scheduled Vehicle <span className="required">*</span></label>
            <input type="text" className="form-input-addrecord dropdown-input" name="scheduled_vehicle" value={formData.scheduled_vehicle} onChange={handleScheduledVehicleChange} onBlur={handleScheduledVehicleBlur} list="scheduled-vehicle-list" required autoComplete="off" placeholder="Select or enter truck number" />
            <datalist id="scheduled-vehicle-list">{filteredScheduledVehicles.map((vehicle) => (<option key={vehicle} value={vehicle} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Carrier <span className="required">*</span></label>
            <input type="text" className="form-input-addrecord dropdown-input" name="carrier" value={formData.carrier} onChange={handleChange} list="carrier-list" required autoComplete="off" placeholder="Auto-filled or enter carrier" />
            <datalist id="carrier-list">{dropdownData.carriers.map((carrier) => (<option key={carrier} value={carrier} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Next Point</label>
            <input type="text" className="form-input-addrecord dropdown-input" name="next_point" value={formData.next_point} onChange={handleChange} list="next-point-list" autoComplete="off" />
            <datalist id="next-point-list">{dropdownData.nextPoints.map((point, index) => (<option key={index} value={point} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Next Arrival Date & Time</label>
            <input type="datetime-local" className="form-input-addrecord" name="next_arrival_date" value={formData.next_arrival_date} onChange={handleChange} />
          </div>
          <div>
            <label className="form-label-addrecord">IVMS Point</label>
            <input type="text" className="form-input-addrecord dropdown-input" name="ivms_point" value={formData.ivms_point} onChange={handleChange} list="ivms-point-list" autoComplete="off" />
            <datalist id="ivms-point-list">{dropdownData.ivmsPoints.map((point, index) => (<option key={index} value={point} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">IVMS Check Date & Time</label>
            <input type="datetime-local" className="form-input-addrecord" name="ivms_check_date" value={formData.ivms_check_date} onChange={handleChange} />
          </div>
          <div>
            <label className="form-label-addrecord">Offload Point <span className="required">*</span></label>
            <input type="text" className="form-input-addrecord dropdown-input" name="offload_point" value={formData.offload_point} onChange={handleChange} list="offload-point-list" required autoComplete="off" />
            <datalist id="offload-point-list">{dropdownData.offloadPoints.map((point, index) => (<option key={index} value={point} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Destination</label>
            <input type="text" className="form-input-addrecord dropdown-input" name="destination" value={formData.destination} onChange={handleChange} list="destination-list" autoComplete="off" />
            <datalist id="destination-list">{dropdownData.destinations.map((destination, index) => (<option key={index} value={destination} />))}</datalist>
          </div>
          <div className="driver-select-container">
            <label className="form-label-addrecord">Driver Name <span className="required">*</span></label>
            <div className="custom-dropdown">
              <input type="text" className="form-input-addrecord" value={formData.driver_name} onClick={() => setShowDriverDropdown(true)} readOnly placeholder="Click to select driver" />
              {showDriverDropdown && (
                <div className="driver-dropdown-menu">
                  <div className="driver-search-box">
                    <input type="text" placeholder="Search driver..." value={driverSearchTerm} onChange={(e) => setDriverSearchTerm(e.target.value)} onClick={(e) => e.stopPropagation()} className="driver-search-input" />
                  </div>
                  <div className="driver-options-container">
                    {filteredDrivers.length > 0 ? (
                      filteredDrivers.map((driver, index) => {
                        const { className, badge, selectable } = getDriverStatusInfo(driver.driver_name);
                        return (
                          <div key={index} className={`driver-option ${className} ${selectedDriverTemp === driver.driver_name ? "selected" : ""}`} onClick={() => {
                            if (selectable) {
                              setSelectedDriverTemp(driver.driver_name);
                              return;
                            }
                            const violation = findActiveDriverViolation(driver);
                            if (violation) {
                              showDriverViolationAlert(driver, violation);
                              setShowDriverDropdown(false);
                            }
                          }} style={{ cursor: selectable ? 'pointer' : 'not-allowed', opacity: selectable ? 1 : 0.6 }}>
                            <div className="driver-name">{driver.driver_name}{badge}</div>
                            {driver.reporting_driver_code && (<div className="driver-code">({driver.reporting_driver_code})</div>)}
                          </div>
                        );
                      })
                    ) : (<div className="no-results">No drivers found</div>)}
                  </div>
                  <div className="driver-dropdown-actions">
                    <button type="button" className="driver-select-btn" onClick={handleDriverSelection}>OK</button>
                    <button type="button" className="driver-cancel-btn" onClick={() => { setShowDriverDropdown(false); setDriverSearchTerm(""); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="form-label-addrecord">Accommodation</label>
            <input type="text" className="form-input-addrecord" name="accommodation" value={formData.accommodation} onChange={handleChange} list="accommodation-list" />
            <datalist id="accommodation-list">{dropdownData.accommodation.map((accommodation, index) => (<option key={index} value={accommodation} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Sr. Journey Manager <span className="required">*</span></label>
            <input type="text" className="form-input-addrecord" name="sjm" value={formData.sjm} onChange={handleChange} list="sjm-list" required />
            <datalist id="sjm-list">{sjmOptions.map((sjm, index) => (<option key={index} value={sjm} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Truck Status</label>
            <input type="text" className="form-input-addrecord" name="truck_status" value={formData.truck_status} onChange={handleChange} list="truckStatus-list" />
            <datalist id="truckStatus-list">{truckOptions.map((truck_status, index) => (<option key={index} value={truck_status} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Tracker</label>
            <input type="text" className="form-input-addrecord" name="tracker" value={formData.tracker} onChange={handleChange} list="tracker-list" />
            <datalist id="tracker-list">{trackerOptions.map((tracker, index) => (<option key={index} value={tracker} />))}</datalist>
          </div>
          <div>
            <label className="form-label-addrecord">Trip Region<span className="required">*</span></label>
            <input type="text" className="form-input-addrecord" name="start_region" value={formData.start_region} onChange={handleChange} list="start_region-list" required />
            <datalist id="start_region-list">{regionOptions.map((region, index) => (<option key={index} value={region} />))}</datalist>
          </div>

          <div className="addrecord-equipment-section">
            <div className="addrecord-equipment-title">
              <FaShieldAlt className="addrecord-shield-icon" />
              <div>
                <h3>Safety Equipment Handover (Device Tracker)</h3>
                <p>Issue an H₂S monitor and/or Thuraya satellite phone directly with this Journey Plan.</p>
              </div>
            </div>

            <div className="addrecord-equipment-toggles">
              <label className={`addrecord-equip-toggle ${issueH2S ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={issueH2S}
                  onChange={(e) => {
                    setIssueH2S(e.target.checked);
                    if (!e.target.checked) {
                      setH2sData({ device_id: "", phone_number: "", expected_return_at: "", condition_out: "GOOD", remarks: "", briefing_confirmed: false });
                    }
                  }}
                />
                <span>Issue H₂S Monitor</span>
              </label>

              <label className={`addrecord-equip-toggle ${issueThuraya ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={issueThuraya}
                  onChange={(e) => {
                    setIssueThuraya(e.target.checked);
                    if (!e.target.checked) {
                      setThurayaData({ device_id: "", phone_number: "", expected_return_at: "", condition_out: "GOOD", remarks: "", briefing_confirmed: false });
                    }
                  }}
                />
                <span>Issue Thuraya Satellite Phone</span>
              </label>
            </div>

            {issueH2S && (
              <div className="addrecord-equip-card">
                <h4>H₂S Monitor Handover Details</h4>
                <div className="addrecord-equip-fields">
                  <div>
                    <label className="form-label-addrecord">Available H₂S Device <span className="required">*</span></label>
                    <select
                      className="form-input-addrecord form-select-addrecord"
                      value={h2sData.device_id}
                      onChange={(e) => setH2sData({ ...h2sData, device_id: e.target.value })}
                      required={issueH2S}
                    >
                      <option value="">Select H₂S Device ({availableH2S.length} available)</option>
                      {availableH2S.map((d) => {
                        const isExpired = d.calibration_expiry && new Date(d.calibration_expiry) < new Date();
                        const sn = d.serial_number ? ` | SN: ${d.serial_number}` : "";
                        const exp = d.calibration_expiry ? ` | Exp: ${new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(d.calibration_expiry))}` : "";
                        return (
                          <option key={d.device_id} value={d.device_id}>
                            {d.device_number} — ({d.device_type}){sn}{exp}{isExpired ? " ⚠️ [CALIBRATION EXPIRED]" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="form-label-addrecord">Driver Phone Number</label>
                    <input
                      type="tel"
                      className="form-input-addrecord"
                      placeholder="e.g. 96891234567"
                      value={h2sData.phone_number}
                      onChange={(e) => setH2sData({ ...h2sData, phone_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label-addrecord">Expected Return Date & Time</label>
                    <input
                      type="datetime-local"
                      className="form-input-addrecord"
                      value={h2sData.expected_return_at}
                      onChange={(e) => setH2sData({ ...h2sData, expected_return_at: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label-addrecord">Condition When Issued</label>
                    <select
                      className="form-input-addrecord form-select-addrecord"
                      value={h2sData.condition_out}
                      onChange={(e) => setH2sData({ ...h2sData, condition_out: e.target.value })}
                    >
                      <option value="GOOD">GOOD</option>
                      <option value="FAIR">FAIR</option>
                      <option value="DAMAGED">DAMAGED</option>
                    </select>
                  </div>
                  <div className="wide-field">
                    <label className="form-label-addrecord">Remarks / Notes</label>
                    <input
                      type="text"
                      className="form-input-addrecord"
                      placeholder="Optional remarks"
                      value={h2sData.remarks}
                      onChange={(e) => setH2sData({ ...h2sData, remarks: e.target.value })}
                    />
                  </div>
                </div>

                <div className="addrecord-briefing-box">
                  <div className="addrecord-briefing-head">
                    <FaShieldAlt />
                    <div>
                      <strong>1. H₂S Monitor Briefing – Before H₂S Device/Monitor Handover</strong>
                      <p>The JM should brief the driver on:</p>
                    </div>
                  </div>
                  <ul className="addrecord-briefing-list">
                    <li>Where the driver is going and the H₂S risk at the destination.</li>
                    <li>How to use the H₂S device/monitor.</li>
                    <li>How to respond in case of an H₂S alarm.</li>
                  </ul>
                  <label className="addrecord-briefing-check">
                    <input
                      type="checkbox"
                      required={issueH2S}
                      checked={h2sData.briefing_confirmed}
                      onChange={(e) => setH2sData({ ...h2sData, briefing_confirmed: e.target.checked })}
                    />
                    <span>
                      <strong>JM Confirmation:</strong> I confirm that the driver has been briefed on the H₂S requirements before the H₂S device/monitor was handed over. <span className="required">*</span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {issueThuraya && (
              <div className="addrecord-equip-card">
                <h4>Thuraya Satellite Phone Handover Details</h4>
                <div className="addrecord-equip-fields">
                  <div>
                    <label className="form-label-addrecord">Available Thuraya Phone <span className="required">*</span></label>
                    <select
                      className="form-input-addrecord form-select-addrecord"
                      value={thurayaData.device_id}
                      onChange={(e) => setThurayaData({ ...thurayaData, device_id: e.target.value })}
                      required={issueThuraya}
                    >
                      <option value="">Select Thuraya Phone ({availableThuraya.length} available)</option>
                      {availableThuraya.map((d) => {
                        const isExpired = d.calibration_expiry && new Date(d.calibration_expiry) < new Date();
                        const sn = d.serial_number ? ` | SN: ${d.serial_number}` : "";
                        const exp = d.calibration_expiry ? ` | Exp: ${new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(d.calibration_expiry))}` : "";
                        return (
                          <option key={d.device_id} value={d.device_id}>
                            {d.device_number} — ({d.device_type}){sn}{exp}{isExpired ? " ⚠️ [CALIBRATION EXPIRED]" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="form-label-addrecord">Driver Phone Number</label>
                    <input
                      type="tel"
                      className="form-input-addrecord"
                      placeholder="e.g. 96891234567"
                      value={thurayaData.phone_number}
                      onChange={(e) => setThurayaData({ ...thurayaData, phone_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label-addrecord">Expected Return Date & Time</label>
                    <input
                      type="datetime-local"
                      className="form-input-addrecord"
                      value={thurayaData.expected_return_at}
                      onChange={(e) => setThurayaData({ ...thurayaData, expected_return_at: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label-addrecord">Condition When Issued</label>
                    <select
                      className="form-input-addrecord form-select-addrecord"
                      value={thurayaData.condition_out}
                      onChange={(e) => setThurayaData({ ...thurayaData, condition_out: e.target.value })}
                    >
                      <option value="GOOD">GOOD</option>
                      <option value="FAIR">FAIR</option>
                      <option value="DAMAGED">DAMAGED</option>
                    </select>
                  </div>
                  <div className="wide-field">
                    <label className="form-label-addrecord">Remarks / Notes</label>
                    <input
                      type="text"
                      className="form-input-addrecord"
                      placeholder="Optional remarks"
                      value={thurayaData.remarks}
                      onChange={(e) => setThurayaData({ ...thurayaData, remarks: e.target.value })}
                    />
                  </div>
                </div>

                <div className="addrecord-briefing-box">
                  <div className="addrecord-briefing-head">
                    <FaShieldAlt />
                    <div>
                      <strong>2. Thuraya Satellite Phone Briefing – Before Thuraya Handover</strong>
                      <p>The JM should brief the driver on:</p>
                    </div>
                  </div>
                  <ul className="addrecord-briefing-list">
                    <li>Why and where the Thuraya satellite phone is required.</li>
                    <li>How to operate and use the Thuraya phone.</li>
                    <li>How to make an emergency call when there is no mobile network coverage.</li>
                  </ul>
                  <label className="addrecord-briefing-check">
                    <input
                      type="checkbox"
                      required={issueThuraya}
                      checked={thurayaData.briefing_confirmed}
                      onChange={(e) => setThurayaData({ ...thurayaData, briefing_confirmed: e.target.checked })}
                    />
                    <span>
                      <strong>JM Confirmation:</strong> I confirm that the driver has been briefed on the use of the Thuraya satellite phone before the phone was handed over. <span className="required">*</span>
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={submitting} className="submit-button-addrecord">{submitting ? "Submitting..." : "Add Record"}</button>
          {message && <div className="message-addrecord">{message}</div>}
        </form>
      )}

      {showRestAlert && alertDriver && (
        <div className="addrecord-modal-backdrop">
          <div className="addrecord-modal-content">
            <header className="addrecord-modal-header">
              <h2><span role="img" aria-label="warning">⚠️</span> {alertDriver.onRest ? "Driver on Rest Period" : "Driver Rest Required"}</h2>
              <button className="addrecord-modal-close-btn" onClick={() => { setShowRestAlert(false); setAlertDriver(null); }}>×</button>
            </header>
            <main className="addrecord-modal-body">
              <div style={{ textAlign: 'center', marginBottom: '20px' }}><strong style={{ fontSize: '18px' }}>{alertDriver.name}</strong></div>
              {alertDriver.onRest ? (
                <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px' }}>
                  <p>🛌 <strong>Driver is currently on a rest period</strong></p>
                  {alertDriver.hoursRemaining && (<p>Rest ends in: <strong>{alertDriver.hoursRemaining} hours</strong></p>)}
                  <p>Rest is still in progress; journey assignment is allowed.</p>
                </div>
              ) : (
                <div style={{ background: '#f8d7da', padding: '15px', borderRadius: '8px' }}>
                  <p>⚠️ <strong>This driver has worked {alertDriver.days} consecutive days</strong></p>
                  {alertDriver.lastRest && (<p>Last rest: {new Date(alertDriver.lastRest).toLocaleDateString()}</p>)}
                  <p>A 24-hour rest period is required for this Expat driver before journey assignment.</p>
                  <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>Please contact admin if this is an emergency situation.</p>
                </div>
              )}
            </main>
            <footer className="addrecord-modal-footer">
              {!alertDriver.onRest && (<button className="addrecord-modal-btn-primary" onClick={() => { setShowRegisterRest(true); setShowRestAlert(false); }}>Register Rest Period</button>)}
              <button className="addrecord-modal-btn-secondary" onClick={() => { setFormData(prev => ({ ...prev, driver_name: "", driverId: null })); setShowRestAlert(false); setAlertDriver(null); }}>Select Different Driver</button>
            </footer>
          </div>
        </div>
      )}

      {violationAlert && (
        <div className="addrecord-modal-backdrop">
          <div className="addrecord-modal-content" role="alertdialog" aria-modal="true" aria-labelledby="violation-alert-title">
            <header className="addrecord-modal-header">
              <h2 id="violation-alert-title">Journey Assignment Not Allowed</h2>
              <button type="button" className="addrecord-modal-close-btn" aria-label="Close" onClick={() => setViolationAlert(null)}>×</button>
            </header>
            <main className="addrecord-modal-body">
              <div className="driver-violation-alert">
                <p><strong>{violationAlert.driverName}</strong>{violationAlert.driverCode ? ` (${violationAlert.driverCode})` : ""}</p>
                <p>This driver has an active violation and cannot be assigned to a new journey until the violation is resolved.</p>
                <dl className="driver-violation-details">
                  <div><dt>Violation</dt><dd>{violationAlert.violationType}</dd></div>
                  <div><dt>Status</dt><dd>{violationAlert.status}</dd></div>
                  {violationAlert.violationDate && <div><dt>Reported</dt><dd>{new Date(violationAlert.violationDate).toLocaleString()}</dd></div>}
                  {violationAlert.remarks && <div><dt>Remarks</dt><dd>{violationAlert.remarks}</dd></div>}
                </dl>
                <p className="driver-violation-guidance">Please resolve or close the violation in Driver Violations before assigning this driver.</p>
              </div>
            </main>
            <footer className="addrecord-modal-footer">
              <button type="button" className="addrecord-modal-btn-secondary" onClick={() => {
                setFormData(prev => ({ ...prev, driver_name: "", driverId: null, reporting_driver_code: "" }));
                setSelectedDriverTemp("");
                setViolationAlert(null);
              }}>Select Different Driver</button>
            </footer>
          </div>
        </div>
      )}

      {showRegisterRest && alertDriver && (
        <div className="addrecord-modal-backdrop">
          <div className="addrecord-modal-content">
            <header className="addrecord-modal-header"><h2>Register Driver Rest</h2><button className="addrecord-modal-close-btn" onClick={() => { setShowRegisterRest(false); setAlertDriver(null); }}>×</button></header>
            <form onSubmit={handleRegisterRest}>
              <div><label>Driver Name</label><input value={alertDriver.name} readOnly /></div>
              <div><label>Rest Start Date & Time (24 hours minimum)</label><input type="datetime-local" name="rest_start_date" defaultValue={getCurrentLocalDateTime()} required /></div>
              <div><label>Rest End Date & Time</label><input type="datetime-local" name="rest_end_date" /><small style={{ color: '#666', display: 'block' }}>Leave empty for 24-hour rest</small></div>
              <div style={{ marginTop: '16px' }}><button type="submit" disabled={submitting}>{submitting ? "Registering..." : "Register Rest"}</button><button type="button" onClick={() => { setShowRegisterRest(false); setAlertDriver(null); }} style={{ marginLeft: '8px' }}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddRecord;
